import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '../stores/authStore';
import { getJoinTenantContext } from './joinTenantContext';
import { toApiError } from './errors';

/**
 * Refresh-token coordination — at most one in-flight refresh; concurrent 401s
 * await the same promise then retry their original request with the new token.
 */
type RefreshFn = (refreshToken: string) => Promise<{ accessToken: string; refreshToken?: string }>;

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Inject the standard headers every API call needs:
 *   - `Authorization: Bearer <accessToken>` (when authenticated)
 *   - `x-tenant-id`   — the tenant this call is addressed to (regional API only; ignored by
 *                       Core). A caller working inside another community sets an explicit
 *                       override, which wins: the active session's tenant is rewritten by any
 *                       domain resolution, so it cannot be trusted to still name the tenant the
 *                       request belongs to.
 */
function attachAuthHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const auth = useAuthStore.getState();

  // Only pin a template when the build configured one; otherwise let the backend
  // resolve the tenant's effective member template (avoids forcing an empty/invalid pin).
  if (env.appTemplateId) {
    config.headers.set('x-app-template-id', env.appTemplateId);
  }

  const token = auth.accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // A call that names its own tenant wins. Signup pins itself to the community whose member
  // types it listed, and the ambient store is not stable enough to override it — every
  // /resolve-domain rewrites it, which silently moved signup to the wrong community mid-form.
  if (!config.headers.has('x-tenant-id')) {
    const tenantId = getJoinTenantContext() ?? auth.tenantId;
    if (tenantId) {
      config.headers.set('x-tenant-id', tenantId);
    }
  }

  return config;
}

interface RegisterRefreshOptions {
  /** Implementation of POST /auth/refresh — supplied by the api module that owns it. */
  refresh: RefreshFn;
  /** Called when refresh fails terminally — typically routes the user to login. */
  onRefreshFailed?: () => void;
}

/**
 * Wire the standard request + response interceptors onto an axios client.
 * Both the Core API and Tenant (regional) API clients call this — the only
 * difference between them is which `refresh` they pass in.
 */
export function installInterceptors(client: AxiosInstance, options: RegisterRefreshOptions): void {
  client.interceptors.request.use(attachAuthHeaders, (error) => Promise.reject(error));

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
      const status = error.response?.status;

      const isAuthEndpoint =
        original?.url?.includes('/auth/login') ||
        original?.url?.includes('/portal-auth/login') ||
        original?.url?.includes('/auth/refresh');

      if (status === 401 && original && !original._retried && !isAuthEndpoint) {
        original._retried = true;

        const newToken = await coordinatedRefresh(options);
        if (newToken) {
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
          return client.request(original);
        }

        options.onRefreshFailed?.();
      }

      return Promise.reject(toApiError(error));
    },
  );
}

async function coordinatedRefresh(options: RegisterRefreshOptions): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;

  const auth = useAuthStore.getState();
  const refreshToken = auth.refreshToken;
  if (!refreshToken) return null;

  inflightRefresh = (async () => {
    try {
      const result = await options.refresh(refreshToken);
      // `setTokens` rather than `setAuth`: refresh endpoints return tokens only,
      // and the cached user stays as-is.
      await useAuthStore.getState().setTokens(result.accessToken, result.refreshToken);
      return result.accessToken;
    } catch (err) {
      console.warn('[interceptors] refresh failed:', err);
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}
